// Pro API - Core pro operations
// Re-exports types and provides backward-compatible proApi interface
import { request, uploadFile } from "./base";
import { projectsApi } from "./pro/projects";
import { photosApi } from "./pro/photos";
import { roomsApi } from "./pro/rooms";
import { mediaApi } from "./pro/media";
import { teamApi } from "./pro/team";

import { analyticsApi } from "./pro/analytics";
import { blogImagesApi } from "./blog-images";
import type { Pro } from "./pro/types";
import type {
	Blog,
	BlogPro,
	BlogTag,
	ProBlogSuggestion,
	SubmitBlogSuggestionInput,
	CreateBlogInput,
	UpdateBlogInput,
} from "./blogs";

// Re-export all types for backward compatibility
export type {
	Pro,
	Project,
	ProjectPhoto,
	Room,
	Media,
	TeamMember,
	TeamInvitation,
	ContactCheckResult,
} from "./pro/types";

export type {
	BlogImage,
	PexelsPhoto,
	PexelsSearchResult,
	UploadBlogImageInput,
	AddPexelsImageInput,
	AddProjectImageInput,
	UpdateBlogImageInput,
} from "./blog-images";

export { crmApi } from "./pro/crm";
export type {
	PipelineStage,
	LeadSource,
	Lead,
	LeadActivity,
	LeadDocument,
	LeadReminder,
	CrmSettings,
	KanbanData,
	CrmDashboard,
	ReminderCounts,
	CreateLeadInput,
	UpdateLeadInput,
	AddNoteInput,
	ChangeStageInput,
} from "./pro/crm";

export type {
	WebsiteTemplate,
	ProWebsite,
	WebsiteBuildJob,
	WebsiteCustomizations,
	WebsiteSettingsResponse,
	BuildStatusResponse,
	PreviewTokenResponse,
	UpdateWebsiteInput,
	SetCustomDomainResponse,
	VerifyDomainResponse,
} from "./pro/website";
export { websiteApi } from "./pro/website";

// Backward-compatible proApi that delegates to split modules
export const proApi = {
	// Core pro operations
	async getMyRoles() {
		return request<{
			isAdmin: boolean;
			isSuperAdmin: boolean;
			platformRoles: string[];
		}>(`/api/pro/me/roles`);
	},

	async getMyPro() {
		return request<{
			pro: Pro | null;
			proId: string | null;
			proRole: string | null;
			onboardingRequired: boolean;
			pendingInvitation?: { token: string };
		}>(`/api/pro/me`);
	},

	async getProfile(proId: string) {
		return request<Pro>(`/api/pro/${proId}`);
	},

	async updateProfile(proId: string, data: Partial<Pro>) {
		return request<Pro>(`/api/pro/${proId}`, {
			method: "PUT",
			body: data,
		});
	},

	// Projects - delegate to projectsApi
	listProjects: projectsApi.list,
	getProject: projectsApi.get,
	createProject: projectsApi.create,
	updateProject: projectsApi.update,
	deleteProject: projectsApi.delete,
	publishProject: projectsApi.publish,
	archiveProject: projectsApi.archive,
	unarchiveProject: projectsApi.unarchive,

	// Photos - delegate to photosApi
	getProjectPhotos: photosApi.list,
	addProjectPhoto: photosApi.add,
	uploadProjectPhoto: photosApi.upload,
	updateProjectPhoto: photosApi.update,
	deleteProjectPhoto: photosApi.delete,
	reorderProjectPhotos: photosApi.reorder,

	// Rooms - delegate to roomsApi
	getRooms: roomsApi.list,
	getRoom: roomsApi.get,
	createRoom: roomsApi.create,
	updateRoom: roomsApi.update,
	deleteRoom: roomsApi.delete,
	reorderRooms: roomsApi.reorder,
	setDefaultRoom: roomsApi.setDefault,

	// Media - delegate to mediaApi
	getMediaByRoom: mediaApi.listByRoom,
	createMedia: mediaApi.create,
	createMediaBulk: mediaApi.createBulk,
	updateMedia: mediaApi.update,
	deleteMedia: mediaApi.delete,
	moveMedia: mediaApi.move,
	reorderMedia: mediaApi.reorder,
	setRoomCover: mediaApi.setRoomCover,

	// Team - delegate to teamApi
	getTeamMembers: teamApi.getMembers,
	addTeamMember: teamApi.addMember,
	updateTeamMember: teamApi.updateMember,
	removeTeamMember: teamApi.removeMember,
	deactivateTeamMember: teamApi.deactivateMember,
	reactivateTeamMember: teamApi.reactivateMember,
	checkTeamContact: teamApi.checkContact,
	inviteTeamMember: teamApi.invite,
	getTeamInvitations: teamApi.getInvitations,
	resendInvitation: teamApi.resendInvitation,
	cancelInvitation: teamApi.cancelInvitation,

	// Analytics - delegate to analyticsApi
	getProStats: analyticsApi.getProStats,

	// Blog Approvals
	async getPendingBlogApprovals(proId: string) {
		return request<BlogPro[]>(`/api/pro/${proId}/blogs/pending`);
	},

	async getBlogForApproval(proId: string, blogId: string) {
		return request<{ blog: Blog; proAttribution: BlogPro }>(
			`/api/pro/${proId}/blogs/${blogId}/preview`,
		);
	},

	async approveBlog(proId: string, blogId: string) {
		return request<BlogPro>(
			`/api/pro/${proId}/blogs/${blogId}/approve`,
			{
				method: "POST",
			},
		);
	},

	async declineBlog(proId: string, blogId: string, reason: string) {
		return request<BlogPro>(
			`/api/pro/${proId}/blogs/${blogId}/decline`,
			{
				method: "POST",
				body: { reason },
			},
		);
	},

	async requestBlogRewrite(proId: string, blogId: string, feedback: string) {
		return request<BlogPro>(
			`/api/pro/${proId}/blogs/${blogId}/rewrite`,
			{
				method: "POST",
				body: { feedback },
			},
		);
	},

	// Blog Features
	async getMyBlogFeatures(proId: string) {
		return request<Array<Blog & { attribution: BlogPro }>>(
			`/api/pro/${proId}/blogs`,
		);
	},

	// Blog Suggestions
	async submitBlogSuggestion(
		proId: string,
		data: SubmitBlogSuggestionInput,
	) {
		return request<ProBlogSuggestion>(
			`/api/pro/${proId}/blogs/suggestions`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async getMyBlogSuggestions(proId: string) {
		return request<ProBlogSuggestion[]>(
			`/api/pro/${proId}/blogs/suggestions`,
		);
	},

	// Blog slug availability check
	async checkBlogSlug(proId: string, slug: string, excludeId?: string) {
		const params = new URLSearchParams({ slug });
		if (excludeId) params.set("excludeId", excludeId);
		return request<{ available: boolean }>(
			`/api/pro/${proId}/blogs/my-blogs/check-slug?${params.toString()}`,
		);
	},

	// Pro Blog Authoring (NEW)
	async listMyBlogs(proId: string, filters?: { status?: string }) {
		const params = new URLSearchParams();
		if (filters?.status) params.set("status", filters.status);
		const query = params.toString() ? `?${params.toString()}` : "";
		return request<Blog[]>(`/api/pro/${proId}/blogs/my-blogs${query}`);
	},

	async getMyBlog(proId: string, blogId: string) {
		return request<Blog>(`/api/pro/${proId}/blogs/my-blogs/${blogId}`);
	},

	async createMyBlog(proId: string, data: CreateBlogInput) {
		return request<Blog>(`/api/pro/${proId}/blogs/my-blogs`, {
			method: "POST",
			body: data,
		});
	},

	async updateMyBlog(proId: string, blogId: string, data: UpdateBlogInput) {
		return request<Blog>(`/api/pro/${proId}/blogs/my-blogs/${blogId}`, {
			method: "PUT",
			body: data,
		});
	},

	async listBlogTags(proId: string) {
		return request<BlogTag[]>(`/api/pro/${proId}/blogs/blog-tags`);
	},

	async createBlogTag(proId: string, data: { name: string }) {
		return request<BlogTag>(`/api/pro/${proId}/blogs/blog-tags`, {
			method: "POST",
			body: data,
		});
	},

	async deleteMyBlog(proId: string, blogId: string) {
		return request<{ success: boolean }>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}`,
			{
				method: "DELETE",
			},
		);
	},

	async submitMyBlog(proId: string, blogId: string) {
		return request<Blog>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/submit`,
			{
				method: "POST",
			},
		);
	},

	async archiveMyBlog(proId: string, blogId: string) {
		return request<Blog>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/archive`,
			{
				method: "POST",
			},
		);
	},

	async unarchiveMyBlog(proId: string, blogId: string) {
		return request<Blog>(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/unarchive`,
			{
				method: "POST",
			},
		);
	},

	async uploadBlogCover(
		proId: string,
		blogId: string,
		file: File,
		altText?: string,
	) {
		return uploadFile(
			`/api/pro/${proId}/blogs/my-blogs/${blogId}/upload-cover`,
			file,
			altText ? { altText } : undefined,
		);
	},

	async submitFeedback(data: {
		category: string;
		message: string;
		pageUrl: string;
		browser: string;
		screenResolution: string;
		location: string;
	}) {
		return request<{ id: number; message: string }>("/api/pro/feedback", {
			method: "POST",
			body: data,
		});
	},

	// Blog Images - delegate to blogImagesApi
	getBlogImages: blogImagesApi.getBlogImages,
	uploadBlogImage: blogImagesApi.uploadBlogImage,
	addPexelsImage: blogImagesApi.addPexelsImage,
	addProjectImage: blogImagesApi.addProjectImage,
	searchPexels: blogImagesApi.searchPexels,
	deleteBlogImage: blogImagesApi.deleteImage,
	updateBlogImage: blogImagesApi.updateImage,
	getBlogImageUrl: blogImagesApi.getImageUrl,
	validateAltText: blogImagesApi.validateAltText,
};
